import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Building2, MapPin, Globe, Bookmark, EyeOff, ShieldCheck } from "lucide-react";

export default function CompanyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/companies/${id}`);
        setC(data.company);
      } catch (e) {
        if (e.response?.status === 402) { setLocked(true); return; }
        toast.error(formatApiError(e.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, [id]);

  const patch = async (field, value) => {
    try { await api.patch(`/companies/${id}`, { [field]: value }); setC((prev) => ({ ...prev, [field]: value })); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const verifyEmails = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post(`/companies/${id}/verify`);
      if (data.verified) {
        toast.success(`Found ${data.found} verified addresses on the site`);
        setC((prev) => ({ ...prev, emails: data.emails, emails_verified: true }));
      } else {
        toast.info("Couldn't find on-domain addresses — keeping AI suggestions.");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setVerifying(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[#5C6661]">Loading…</div>;

  if (locked) {
    return (
      <div className="flex min-h-screen bg-[#FDFDFB]">
        <Sidebar />
        <main className="flex-1 max-w-3xl mx-auto px-6 md:px-10 py-16 w-full">
          <Card className="p-10 border-[#E2E8E4] text-center" data-testid="locked-card">
            <div className="overline">Locked</div>
            <h1 className="font-display text-2xl font-bold tracking-tight mt-2">This match is Pro-only</h1>
            <p className="text-sm text-[#5C6661] mt-2">You're on the free plan. Upgrade to JobKnocker Pro to see this company and every other match in your list.</p>
            <Link to="/pricing"><Button className="mt-6 rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]">Upgrade to Pro — £4.99/mo</Button></Link>
          </Card>
        </main>
      </div>
    );
  }

  if (!c) return null;

  return (
    <div className="flex min-h-screen bg-[#FDFDFB]">
      <Sidebar />
      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 py-10 w-full">
        <button onClick={() => nav(-1)} className="text-xs text-[#5C6661] hover:text-[#121413] inline-flex items-center gap-1" data-testid="back-btn"><ArrowLeft className="w-3 h-3" /> Back</button>
        <div className="mt-4 flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#E7F0EB] flex items-center justify-center"><Building2 className="w-6 h-6 text-[#2D4A3E]" /></div>
          <div className="flex-1">
            <div className="overline">{c.industry || "Company"}</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-2" data-testid="company-name">{c.name}</h1>
            <div className="flex items-center gap-4 text-sm text-[#5C6661] mt-1">
              {c.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.location}</span>}
              {c.size && <Badge variant="secondary" className="bg-[#F2F4F3] border-transparent text-[#5C6661] font-normal">{c.size}</Badge>}
            </div>
          </div>
        </div>

        {c.reason && (
          <Card className="mt-8 p-6 border-[#E2E8E4]">
            <div className="overline">Why it fits</div>
            <p className="text-sm text-[#121413] mt-2 leading-relaxed">{c.reason}</p>
          </Card>
        )}

        <Card className="mt-6 p-6 border-[#E2E8E4]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="overline flex items-center gap-1.5">Contact addresses {c.emails_verified && <span className="inline-flex items-center gap-1 text-[#2D4A3E] normal-case tracking-normal text-[10px] font-semibold"><ShieldCheck className="w-3 h-3" /> Verified from website</span>}</div>
            {c.website && (
              <Button onClick={verifyEmails} disabled={verifying} size="sm" variant="outline" className="rounded-full border-[#E2E8E4]" data-testid="verify-emails-btn">
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> {verifying ? "Scanning site…" : c.emails_verified ? "Re-verify" : "Verify emails"}
              </Button>
            )}
          </div>
          <ul className="mt-3 space-y-2">
            {(c.emails || []).map((e) => (<li key={e} className="text-sm font-mono text-[#2D4A3E]" data-testid={`company-email-${e}`}>{e}</li>))}
            {(!c.emails || c.emails.length === 0) && <li className="text-sm text-[#5C6661]">No public addresses on file.</li>}
          </ul>
        </Card>

        <div className="flex flex-wrap gap-3 mt-6">
          {c.website && (
            <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-full border-[#E2E8E4]" data-testid="visit-website"><Globe className="w-4 h-4 mr-2" /> Visit website</Button>
            </a>
          )}
          <Button variant={c.saved ? "default" : "outline"} className={c.saved ? "rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]" : "rounded-full border-[#E2E8E4]"} onClick={() => patch("saved", !c.saved)} data-testid="save-btn">
            <Bookmark className="w-4 h-4 mr-2" /> {c.saved ? "Saved" : "Save"}
          </Button>
          <Button variant="outline" className="rounded-full border-[#E2E8E4]" onClick={() => patch("hidden", !c.hidden)} data-testid="hide-btn">
            <EyeOff className="w-4 h-4 mr-2" /> {c.hidden ? "Unhide" : "Hide"}
          </Button>
        </div>
      </main>
    </div>
  );
}
